// Frontend API client — Source create + live preview poll.

import type { Source, DataHubEvent, View, FilterTree, ColumnSpec, Trigger, TriggerAction, TriggerRun, Replay, UserPublic, Workspace, WorkspaceMembership, WorkspaceRole, ApiKeyRedacted, ApiKeyWithValue } from '@datahub/shared';

export const getMe = async (): Promise<UserPublic | null> => {
  const r = await fetch('/api/auth/me');
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = await r.json() as { user: UserPublic };
  return body.user;
};
export const signOut = async (): Promise<void> => {
  await fetch('/api/auth/logout', { method: 'POST' });
};
export const getAuthConfig = async (): Promise<{ google_oauth: boolean; redirect_uri: string }> => {
  const r = await fetch('/api/auth/config');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<{ google_oauth: boolean; redirect_uri: string }>;
};

// ── Workspaces ───────────────────────────────────────────────────────────
export interface WorkspaceWithRole extends Workspace { role?: WorkspaceRole }
export interface MemberRow extends WorkspaceMembership { user: { user_id: string; email: string; name: string; picture?: string } | null }

export const listWorkspaces = async (all = false): Promise<WorkspaceWithRole[]> => {
  const r = await fetch(`/api/workspaces${all ? '?all=1' : ''}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()) as { workspaces: WorkspaceWithRole[] }).workspaces;
};
export const createWorkspace = async (name: string, slug?: string): Promise<Workspace> => {
  const r = await fetch('/api/workspaces', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, slug }),
  });
  if (!r.ok) throw new Error(((await r.json()) as { error?: { code?: string; detail?: string } }).error?.detail ?? `HTTP ${r.status}`);
  return ((await r.json()) as { workspace: Workspace }).workspace;
};
export const selectWorkspace = async (workspaceId: string): Promise<void> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/select`, { method: 'POST' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
};
export const listMembers = async (workspaceId: string): Promise<MemberRow[]> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/members`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()) as { members: MemberRow[] }).members;
};
export const inviteMember = async (workspaceId: string, email: string, role: WorkspaceRole = 'editor'): Promise<WorkspaceMembership> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/members`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  const body = await r.json().catch(() => ({})) as { membership?: WorkspaceMembership; error?: { code?: string } };
  if (!r.ok) throw new Error(body.error?.code ?? `HTTP ${r.status}`);
  return body.membership!;
};
export const removeMember = async (workspaceId: string, membershipId: string): Promise<void> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/members/${membershipId}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
};
export const countUnscopedSources = async (workspaceId: string): Promise<number> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/unscoped-sources`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()) as { count: number }).count;
};
export const claimUnscopedSources = async (workspaceId: string): Promise<{ claimed: number }> => {
  const r = await fetch(`/api/workspaces/${workspaceId}/claim-unscoped`, { method: 'POST' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<{ claimed: number }>;
};
export interface BulkMoveResult {
  moved: string[];
  skipped: Array<{ source_id: string; reason: string }>;
  target_workspace_id: string;
}
export interface TagPair { key: string; value: string; count: number; last_seen_at: string }
export interface TagKey { key: string; count: number; last_seen_at: string }
export const getTagStats = async (): Promise<{ keys: TagKey[]; pairs: TagPair[] }> => {
  const r = await fetch('/api/v1/sources/tags/stats');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<{ keys: TagKey[]; pairs: TagPair[] }>;
};

export const bulkMoveSources = async (sourceIds: string[], targetWorkspaceId: string): Promise<BulkMoveResult> => {
  const r = await fetch('/api/v1/sources/bulk-move', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_ids: sourceIds, target_workspace_id: targetWorkspaceId }),
  });
  const body = await r.json().catch(() => ({})) as BulkMoveResult & { error?: { code?: string; detail?: string } };
  if (!r.ok) throw new Error(body.error?.detail ?? body.error?.code ?? `HTTP ${r.status}`);
  return body;
};

// ── API keys ─────────────────────────────────────────────────────────────
export const listApiKeys = async (workspaceId?: string): Promise<ApiKeyRedacted[]> => {
  const q = workspaceId ? `?workspace_id=${workspaceId}` : '';
  const r = await fetch(`/api/api-keys${q}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()) as { keys: ApiKeyRedacted[] }).keys;
};
export const createApiKey = async (workspaceId: string, name: string): Promise<ApiKeyWithValue> => {
  const r = await fetch('/api/api-keys', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workspace_id: workspaceId, name }),
  });
  const body = await r.json().catch(() => ({})) as { key?: ApiKeyWithValue; error?: { code?: string } };
  if (!r.ok) throw new Error(body.error?.code ?? `HTTP ${r.status}`);
  return body.key!;
};
export const revokeApiKey = async (id: string): Promise<void> => {
  const r = await fetch(`/api/api-keys/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
};

// ── Audit log (super-admin only) ─────────────────────────────────────────
export interface AuditEntryClient {
  audit_id: string;
  user_id: string;
  user_email: string;
  action: string;
  resource_kind?: string;
  resource_id?: string;
  detail?: Record<string, unknown>;
  ip: string;
  created_at: string;
}
export const listAudit = async (opts: { limit?: number; userId?: string; actionPrefix?: string } = {}): Promise<AuditEntryClient[]> => {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.userId) params.set('user_id', opts.userId);
  if (opts.actionPrefix) params.set('action_prefix', opts.actionPrefix);
  const qs = params.toString();
  const r = await fetch(`/api/audit${qs ? `?${qs}` : ''}`);
  if (r.status === 401) throw new Error('unauthenticated');
  if (r.status === 403) throw new Error('forbidden');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()) as { entries: AuditEntryClient[] }).entries;
};

const BASE = '';

// Global rate-limit signal. When the API returns 429, we surface
// `retry_after` seconds via a custom event the UI listens for so a
// dashboard-wide banner can render. Frontend pollers also slow down
// after a 429 to avoid hammering harder.
export const RATE_LIMIT_EVENT = 'datahub:rate-limited';
let lastRateLimitedAt = 0;
function emitRateLimited(retryAfter: number): void {
  lastRateLimitedAt = Date.now();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT, { detail: { retryAfter, at: lastRateLimitedAt } }));
  }
}
export function getLastRateLimitedAt(): number { return lastRateLimitedAt; }

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('retry-after') ?? '5');
    emitRateLimited(retryAfter);
    throw new Error(`rate_limited · retry in ${retryAfter}s`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(err.error?.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('retry-after') ?? '5');
    emitRateLimited(retryAfter);
    throw new Error(`rate_limited · retry in ${retryAfter}s`);
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} — ${path}`);
  }
  return res.json() as Promise<T>;

// ── Sources & inputs ─────────────────────────────────────────────────────

}

export const createSource = (name?: string, tags?: Record<string, string>, connectorProfile?: string) =>
  post<Source>('/api/v1/sources', { name, tags, connector_profile: connectorProfile });

export const getSource = (sourceId: string) =>
  get<Source>(`/api/v1/sources/${sourceId}`);

export const listSources = (limit = 50, includeArchived = false) =>
  get<Source[]>(`/api/v1/sources?limit=${limit}${includeArchived ? '&include_archived=true' : ''}`);

export const patchSource = async (sourceId: string, patch: { name?: string; tags?: Record<string, string>; notes?: string; status?: 'active' | 'paused' | 'archived' }): Promise<Source> => {
  const res = await fetch(`/api/v1/sources/${sourceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(err.error?.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Source>;
};

export const archiveSource = async (sourceId: string): Promise<void> => {
  await fetch(`/api/v1/sources/${sourceId}`, { method: 'DELETE' });
};

export const patchSourceInput = async (
  sourceId: string,
  inputId: string,
  patch: {
    name?: string | null;
    status?: 'active' | 'paused' | 'archived';
    tags?: Record<string, string> | null;
  },
): Promise<import('@datahub/shared').SourceInput> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/inputs/${inputId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = body as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return body as import('@datahub/shared').SourceInput;
};

/** Create an additional webhook input on a source (multi-hook). */
export const createSourceWebhook = async (
  sourceId: string,
  body: { name?: string; tags?: Record<string, string> } = {},
): Promise<import('@datahub/shared').SourceInput> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/inputs/webhook`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = j as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return j as import('@datahub/shared').SourceInput;
};

export const patchSourceSlug = async (sourceId: string, slug: string | null): Promise<{ source_id: string; slug_alias: string | null; slug_history: string[] }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/slug`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = body as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);

// ── Secrets ─────────────────────────────────────────────────────────────

  }
  return body as { source_id: string; slug_alias: string | null; slug_history: string[] };
};

export interface SecretRedactedClient {
  secret_id: string;
  name: string;
  kind: 'bearer' | 'basic' | 'api_key' | 'opaque';
  value_redacted: string;
  description?: string;
  created_at: string;
  updated_at: string;
  last_used_at?: string;
}
export const listSecrets = async (): Promise<SecretRedactedClient[]> => {
  const r = await fetch('/api/v1/secrets');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = await r.json() as { secrets: SecretRedactedClient[] };
  return body.secrets;
};
export const createSecret = async (body: { name: string; kind: SecretRedactedClient['kind']; value: string; description?: string }): Promise<SecretRedactedClient & { value: string }> => {
  const r = await fetch('/api/v1/secrets', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const j = await r.json();
  if (!r.ok) {
    const e = j as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
  }
  return j as SecretRedactedClient & { value: string };
};
export const rotateSecret = async (secretId: string, value: string): Promise<SecretRedactedClient> => {
  const r = await fetch(`/api/v1/secrets/${secretId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value }) });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
  }
  return r.json() as Promise<SecretRedactedClient>;
};
export const revealSecret = async (secretId: string): Promise<{ secret_id: string; name: string; value: string }> => {
  const r = await fetch(`/api/v1/secrets/${secretId}/reveal`, { method: 'POST' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<{ secret_id: string; name: string; value: string }>;
};
export const deleteSecret = async (secretId: string): Promise<void> => {
  const r = await fetch(`/api/v1/secrets/${secretId}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
};
export const getSecretUsage = async (secretId: string): Promise<{ secret_id: string; name: string; used_by: Array<{ trigger_id: string; source_id: string; name: string; action_type: string }> }> => {
  const r = await fetch(`/api/v1/secrets/${secretId}/usage`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();

// ── Form config + slug ──────────────────────────────────────────────────

};

export const patchFormConfig = async (sourceId: string, config: import('@datahub/shared').FormConfig | null): Promise<{ form_config: import('@datahub/shared').FormConfig | null }> => {
  const r = await fetch(`/api/v1/sources/${sourceId}/form-config`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ config }),
  });
  const j = await r.json();
  if (!r.ok) {
    const e = j as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
  }
  return j as { form_config: import('@datahub/shared').FormConfig | null };
};

export const checkSlugAvailability = async (sourceId: string, candidate: string): Promise<{ available: boolean; reason?: string; current?: boolean }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/slug-availability?candidate=${encodeURIComponent(candidate)}`);
  if (!res.ok) return { available: false, reason: `http_${res.status}` };
  return res.json() as Promise<{ available: boolean; reason?: string; current?: boolean }>;
};

// ── Raw uploads ──────────────────────────────────────────────────────────


export interface ApiPullResponse {
  fetched_url: string;
  upstream_status: number;
  accepted: number;
  event_ids: string[];
  source_id: string;
}
export interface RawUpload {
  upload_id: string;
  path: string;
  date: string;
  size_bytes: number;
  created_at: string;
  original_filename: string;
  original_content_type: string;
}
export const listRawUploads = (sourceId: string) =>
  get<RawUpload[]>(`/upload/${sourceId}/raw_uploads`);

export const reprocessRawUpload = async (sourceId: string, uploadId: string, bulk = true): Promise<Record<string, unknown>> => {
  const res = await fetch(`/upload/${sourceId}/raw_uploads/${uploadId}/reprocess${bulk ? '?bulk=true' : ''}`, { method: 'POST' });
  return res.json() as Promise<Record<string, unknown>>;
};

export const rawUploadDownloadUrl = (sourceId: string, uploadId: string): string =>
  `/upload/${sourceId}/raw_uploads/${uploadId}/download`;

export const fetchSignedUrl = async (sourceId: string, uploadId: string, ttlSeconds = 86400): Promise<{ signed_url: string; expires_at: string }> => {
  const res = await fetch(`/upload/${sourceId}/raw_uploads/${uploadId}/signed-url?ttl_seconds=${ttlSeconds}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ signed_url: string; expires_at: string }>;
};


// ── API pull schedules ─────────────────────────────────────────────────

// Schedules

export interface Schedule {
  schedule_id: string;
  source_id: string;
  name: string;
  kind: 'api_pull';
  cron: string;
  config: { url: string; method?: 'GET' | 'POST'; headers?: Record<string, string>; json_path?: string };
  status: 'active' | 'paused';
  last_run_at?: string;
  last_status?: 'success' | 'error';
  last_error?: string;
  last_events_ingested?: number;
  next_run_at?: string;
  created_at: string;
  updated_at: string;
}

export const listSchedules = (sourceId: string) =>
  get<Schedule[]>(`/api/v1/sources/${sourceId}/schedules`);

export const createSchedule = async (sourceId: string, body: Omit<Schedule, 'schedule_id' | 'source_id' | 'created_at' | 'updated_at' | 'last_run_at' | 'last_status' | 'last_error' | 'last_events_ingested' | 'next_run_at'>): Promise<Schedule> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Schedule>;
};

export const patchSchedule = async (sourceId: string, scheduleId: string, patch: Partial<Pick<Schedule, 'name' | 'cron' | 'config' | 'status'>>): Promise<Schedule> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schedules/${scheduleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Schedule>;
};

export const deleteSchedule = async (sourceId: string, scheduleId: string): Promise<void> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schedules/${scheduleId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const runScheduleNow = async (sourceId: string, scheduleId: string): Promise<{ ok: boolean; events_ingested?: number; error?: { code: string; detail?: string } }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schedules/${scheduleId}/run-now`, { method: 'POST' });
  return res.json() as Promise<{ ok: boolean; events_ingested?: number; error?: { code: string; detail?: string } }>;

// ── BigQuery views (per-source dataset) ───────────────────────────────

};

export interface BqView {
  name: string;
  sql: string;
  created_at: string | null;
  modified_at: string | null;
  description: string;
}
export interface BqViewsList {
  source_id: string;
  dataset: string;
  views: BqView[];
}
export const listBqViews = (sourceId: string) => get<BqViewsList>(`/api/v1/sources/${sourceId}/bq-views`);

export const createBqView = async (sourceId: string, body: { name: string; sql: string; description?: string }): Promise<BqView & { source_id: string; dataset: string; fully_qualified: string }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/bq-views`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<BqView & { source_id: string; dataset: string; fully_qualified: string }>;
};

export const updateBqView = async (sourceId: string, viewName: string, body: { sql: string; description?: string }): Promise<BqView> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/bq-views/${viewName}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<BqView>;
};

/** Live SQL preview for the recipe builder — runs SELECT, returns first
 *  N rows + column metadata + BigQuery JobStatistics (KPI #9 — real
 *  cost dimensions: bytes scanned, slot consumption, cache hit). */
export interface BqViewJobStats {
  bytes_processed: number;
  cache_hit: boolean;
  slot_ms: number;
}
export interface BqViewPreviewResult {
  rows: Array<Record<string, unknown>>;
  row_count: number;
  columns: string[];
  job_stats?: BqViewJobStats;
}
export const previewBqView = async (sourceId: string, sql: string, limit = 25): Promise<BqViewPreviewResult> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/bq-views:preview`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sql, limit }),
  });
  const j = await res.json() as BqViewPreviewResult & { error?: { code?: string; detail?: string } };
  if (!res.ok) throw new Error(j.error?.detail ?? j.error?.code ?? `HTTP ${res.status}`);
  return j;
};

export const deleteBqView = async (sourceId: string, viewName: string): Promise<void> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/bq-views/${viewName}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

/** True event count from BigQuery — replaces the 200-row Firestore
 *  preview length that the source header used to surface. Returns
 *  total + last_24h + last_hour + last_event_at in one query. */
export interface EventsCountResponse {
  source_id: string;
  total: number;
  last_24h: number;
  last_hour: number;
  last_event_at: string | null;
  scanned_at: string;
}
export const getEventsCount = (sourceId: string) =>
  get<EventsCountResponse>(`/api/v1/sources/${sourceId}/events:count`);

/** Daily-import dedup analysis (audit finding: high dup_ratio means
 *  operator should configure a merge key). */
export interface DedupAnalysisResponse {
  source_id: string;
  sample_size: number;
  unique_payload_hashes: number;
  dup_ratio: number; // 0..1
  top_repeating_payloads: Array<{ payload_hash: string; occurrences: number }>;
  suggested_merge_keys: Array<{ path: string; cardinality_ratio: number; coverage: number }>;
  scanned_at: string;
}
export const getDedupAnalysis = (sourceId: string) =>
  get<DedupAnalysisResponse>(`/api/v1/sources/${sourceId}/dedup-analysis`);

export interface OrphanStorage {
  checked_at: string;
  active_sources: number;
  archived_sources: number;
  orphan_bq_datasets: Array<{ dataset: string; table_count: number; size_bytes: number }>;
  orphan_bq_tables_in_shared: Array<{ dataset: string; table: string; row_count: number; size_bytes: number }>;
  orphan_gcs_prefixes: Array<{ prefix: string; object_count: number; size_bytes: number }>;
  totals: {
    bq_datasets: number;
    bq_shared_tables: number;
    gcs_prefixes: number;
    bq_size_bytes: number;
    gcs_size_bytes: number;
  };
}
export const getOrphanStorage = () => get<OrphanStorage>('/api/v1/workspace/orphan-storage');
export const cleanupOrphanStorage = async (): Promise<{ cleaned_at: string; dropped: { bq_datasets: string[]; bq_shared_tables: string[]; gcs_prefixes: string[] }; errors: Array<{ kind: string; name: string; error: string }> }> => {
  const res = await fetch('/api/v1/workspace/orphan-storage/cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: 'WIPE-ORPHAN-STORAGE' }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<{ cleaned_at: string; dropped: { bq_datasets: string[]; bq_shared_tables: string[]; gcs_prefixes: string[] }; errors: Array<{ kind: string; name: string; error: string }> }>;
};

export interface TableConflict {
  table_name: string;
  source_count: number;
  sources: string[];
  triggers: number;
  merge_key_paths: string[];
  merge_key_collision: boolean;
}
export const listTableConflicts = () => get<{ checked_at: string; conflicts: TableConflict[] }>('/api/v1/workspace/table-conflicts');

// Find which sources currently write to a given BQ table_name. Empty for
// table names that don't exist yet. Used by the trigger dialog to warn
// operator about fan-in BEFORE they save the trigger.
export interface TableRowsPreview {
  trigger_id: string;
  table_name: string;
  dataset: string;
  fully_qualified: string;
  rows: Array<Record<string, unknown>>;
  total: number;
  columns: Array<{ name: string; type: string }>;
}
export const getTableRowsPreview = async (
  sourceId: string, triggerId: string,
  opts: { search?: string; sort?: { column: string; dir: 'ASC' | 'DESC' }; limit?: number; offset?: number } = {},
): Promise<TableRowsPreview> => {
  const qs = new URLSearchParams();
  if (opts.search) qs.set('search', opts.search);
  if (opts.sort) qs.set('sort', `${opts.sort.column},${opts.sort.dir}`);
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.offset) qs.set('offset', String(opts.offset));
  const url = `/api/v1/sources/${sourceId}/triggers/${triggerId}/rows${qs.toString() ? `?${qs}` : ''}`;
  return get<TableRowsPreview>(url);
};

export const lookupTableUsage = (tableName: string, sourceId?: string) => {
  // Pass sourceId so the server can compute the *effective* fully-
  // qualified BQ table (per-source dataset by default) and only flag
  // REAL collisions. Without sourceId, the server falls back to the
  // legacy 'match by table_name string only' behaviour.
  const qs = new URLSearchParams({ table_name: tableName });
  if (sourceId) qs.set('source_id', sourceId);
  return get<{ table_name: string; caller_fq?: string | null; source_count: number; sources: string[]; triggers: Array<{ trigger_id: string; source_id: string; name: string; merge_key_path?: string; effective_fq?: string }> }>(`/api/v1/workspace/table-usage?${qs.toString()}`);
};

export const pauseAllTriggersWorkspace = async (): Promise<{ workspace_paused_at: string; total_paused: number; per_source: Array<{ source_id: string; source_name: string; paused: number }> }> => {
  const res = await fetch('/api/v1/sources/pause-all-triggers', { method: 'POST' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ workspace_paused_at: string; total_paused: number; per_source: Array<{ source_id: string; source_name: string; paused: number }> }>;
};

export const rotateHmacSecret = async (sourceId: string): Promise<{ source_id: string; hmac_secret: string; rotated_at: string }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/inputs/webhook:rotate-secret`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(err.error?.detail ?? err.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<{ source_id: string; hmac_secret: string; rotated_at: string }>;
};

export const apiPullFetch = async (sourceId: string, body: { url: string; method?: 'GET' | 'POST'; headers?: Record<string, string>; json_path?: string }): Promise<ApiPullResponse> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/inputs/api_pull:fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = await res.json() as ApiPullResponse | { error: { code: string; detail?: string } };
  if (!res.ok || !('accepted' in j)) {
    const err = (j as { error: { code: string; detail?: string } }).error;
    throw new Error(err?.detail ?? err?.code ?? `HTTP ${res.status}`);
  }
  return j;
};

export const getEventsPreview = (sourceId: string, limit = 20) =>
  get<DataHubEvent[]>(`/api/v1/sources/${sourceId}/events:preview?limit=${limit}`);

export interface LineageResponse {
  event_id: string;
  depth: number;
  chain: DataHubEvent[];
}

export const getEventLineage = (sourceId: string, eventId: string) =>
  get<LineageResponse>(`/api/v1/sources/${sourceId}/events/${eventId}/lineage`);

export interface ReprocessResponse {
  event_id: string;
  source_id: string;
  reprocessed_at: string;
  note: string;
}
export const reprocessEvent = async (sourceId: string, eventId: string): Promise<ReprocessResponse> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/events/${eventId}/reprocess`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(err.error?.detail ?? err.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<ReprocessResponse>;

// ── Schema introspection + Suggestions ───────────────────────────────

};

/** Rich path entry from events:schema (default mode after 2026-05-12).
 *  Walks the payload tree to MAX_SCHEMA_DEPTH and tags each entry with
 *  depth + parent + structural flags so the UI can render nested
 *  cells with collapsible blocks (no per-source code). */
export interface SchemaField {
  path: string;
  types: string[];
  sample_values: unknown[];
  occurrences: number;
  /** Depth from root payload. 1 = top-level key. */
  depth?: number;
  /** Dot-path of parent ('' / null for top-level). */
  parent?: string | null;
  /** True when occurrences/sampled_events < SCHEMA_SPARSE_OCCURRENCE_RATIO. */
  sparse?: boolean;
  /** Set when this path is an OBJECT whose distinct child-key count
   *  exceeded SCHEMA_CARDINALITY_COLLAPSE; children weren't enumerated.
   *  UI renders a "+N keys" chip and exposes a drill-in action. */
  collapsed_cardinality?: number;
  /** Runtime JS type of the value at this path. */
  parent_kind?: 'object' | 'array' | 'scalar' | 'null';
}
export interface SchemaResponse {
  source_id: string;
  sampled_events: number;
  total_sampled?: number;
  event_type?: string;
  fields: SchemaField[];
  /** Parent paths whose children were collapsed under the cardinality
   *  threshold. Mirror of the per-field `collapsed_cardinality` flag,
   *  flattened for quick "is this path drillable?" lookup. */
  expandable_parents?: string[];
  max_depth?: number;
  cardinality_collapse_threshold?: number;
  sparse_occurrence_ratio?: number;
}
export const getEventsSchema = (sourceId: string) =>
  get<SchemaResponse>(`/api/v1/sources/${sourceId}/events:schema`);

/** Legacy flat top-level-only schema (?flat=true). Kept for callers
 *  that haven't migrated to the recursive shape yet. */
export const getEventsSchemaFlat = (sourceId: string) =>
  get<SchemaResponse>(`/api/v1/sources/${sourceId}/events:schema?flat=true`);

/** Per-event-type bucketed variant. Used by the SchemaReadyBanner so
 *  each notification can show a field count + first-3-fields preview
 *  before the operator clicks Connect. Server returns `schemas[]` per
 *  the current contract (sources.ts:1454). */
export interface SchemaResponseGroupedBucket {
  event_type: string;
  sampled_events: number;
  fields: SchemaField[];
  expandable_parents?: string[];
}
export interface SchemaResponseGrouped {
  source_id: string;
  sampled_events: number;
  schemas: SchemaResponseGroupedBucket[];
}
export const getEventsSchemaByEventType = (sourceId: string) =>
  get<SchemaResponseGrouped>(`/api/v1/sources/${sourceId}/events:schema?group_by=event_type`);

export interface ViewSuggestion {
  id: string;
  name: string;
  blurb: string;
  color?: string;
  filter: FilterTree;
  estimated_count: number;
  suggested_table_name?: string;
  // Schema-fingerprint grouping (post-2026-04-29 redesign):
  // each suggestion corresponds to a payload-schema bucket. `paths` lists
  // every leaf path the bucket's events share — operator scans this to
  // recognise "is this the Pipedrive deal shape?" without opening a sample.
  // `origin_summary` is a hint chip: "webhook · file_upload · …".
  paths?: string[];
  /** Paths present in 100% of group members. Used to render core
   *  fields solid and optional dimmed in the schema list. */
  core_paths?: string[];
  optional_paths?: string[];
  /** path → count of members that carry it. */
  path_presence?: Record<string, number>;
  origin_summary?: string;
  /** Set when this group attached to a saved Schema Definition. */
  matched_schema_id?: string;
  matched_schema_name?: string;
  matched_schema_tags?: Record<string, string>;
  /** Cross-device AI cache (server-side, 7d TTL). Same group sees the
   *  same auto-name on any device that hits this endpoint. */
  ai_cached_name?: string;
  ai_cached_description?: string;
  ai_cached_tags?: Record<string, string>;
  /** Member samples for group-level diff (event × field matrix). */
  member_samples?: Array<{ event_id: string; received_at: string; paths: string[] }>;
  /** Fingerprint variants that were merged together into this group.
   *  Each variant has its own path set + how many events it covered.
   *  UI renders a variant-level diff to show which fields differ
   *  between the variants Jaccard-merged into this single group. */
  variants?: Array<{ fingerprint_id: string; paths: string[]; count: number }>;
}
export const getViewSuggestions = (sourceId: string) =>
  get<{ source_id: string; sampled: number; suggestions: ViewSuggestion[] }>(`/api/v1/sources/${sourceId}/views:auto-suggest`);

// ── Schema definitions registry ──────────────────────────────────────


export interface NamedSchemaGroup {
  id: string;
  name: string;
  description?: string;
  tags?: Record<string, string>;
}

export interface SchemaDefinition {
  schema_id: string;
  source_id: string;
  workspace_id?: string;
  name: string;
  description?: string;
  tags: Record<string, string>;
  core_paths: string[];
  optional_paths: string[];
  source_fingerprints: string[];
  match_threshold: number;
  member_event_count: number;
  matched_event_count?: number;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export const saveSchemaDef = async (sourceId: string, body: {
  name: string;
  description?: string;
  tags: Record<string, string>;
  core_paths: string[];
  optional_paths?: string[];
  source_fingerprints?: string[];
  member_event_count?: number;
  match_threshold?: number;
}): Promise<SchemaDefinition> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schemas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`save schema failed: HTTP ${res.status}`);
  return res.json();
};

export const listSchemaDefs = (sourceId: string): Promise<SchemaDefinition[]> =>
  get<SchemaDefinition[]>(`/api/v1/sources/${sourceId}/schemas`);

export const archiveSchemaDef = async (sourceId: string, schemaId: string): Promise<void> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schemas/${schemaId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`archive schema failed: HTTP ${res.status}`);
};

export interface SchemaPromoteResult {
  schema_id: string;
  trigger_id: string;
  view_id: string;
  table_name: string;
  note: string;
}

export const promoteSchemaToBQ = async (sourceId: string, schemaId: string, body: {
  table_name?: string;
  view_id?: string;
  merge_key_path?: string;
} = {}): Promise<SchemaPromoteResult> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schemas/${schemaId}/promote-to-bq`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`promote failed: HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json();
};
export const autoNameViewGroups = async (sourceId: string): Promise<NamedSchemaGroup[]> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/views:auto-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`auto-name failed: HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  const body = await res.json() as { named: NamedSchemaGroup[] };
  return body.named ?? [];
};

/** T3.1 + 3.2 schema-evolution drift report — DETERMINISTIC (no LLM).
 *  For every saved Schema Definition, compares recent events' paths to
 *  the schema's core/optional sets. Surfaces new fields the operator
 *  may want to add to canonical and core fields that have stopped
 *  appearing (lifecycle change or upstream regression). */
export interface DriftReport {
  schema_id: string;
  schema_name: string;
  total_events: number;
  matching_events: number;       // ≥ match_threshold × |core|
  near_miss_events: number;      // 0.5 ≤ coverage < threshold
  new_paths: Array<{ path: string; event_count: number; first_seen: string }>;
  vanishing_paths: Array<{ path: string; missing_in: number; was_in_core: boolean }>;
}
export const getSchemaDriftFeed = async (sourceId: string): Promise<DriftReport[]> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schemas:drift-feed`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`drift-feed failed: HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  const body = await res.json() as { reports: DriftReport[] };
  return body.reports ?? [];
};

/** Convenience patcher for "Add to canonical" / "Promote to core"
 *  drift actions. The backend already supports PATCH on schemas; this
 *  is just a typed shortcut. */
export const patchSchemaDef = async (
  sourceId: string,
  schemaId: string,
  patch: Partial<{
    name: string;
    description: string;
    tags: Record<string, string>;
    core_paths: string[];
    optional_paths: string[];
    match_threshold: number;
  }>,
): Promise<SchemaDefinition> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/schemas/${schemaId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`patch schema failed: HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json();
};

/** T2.1 LLM merge oracle. Operator clicks "🤖 propose merges" → backend
 *  asks Gemini "which fingerprint groups represent the SAME logical
 *  entity, just with shape variations?" Returns conservative proposals
 *  the operator confirms one-by-one. AI on-request only. */
export interface MergeProposal {
  group_ids: string[];
  suggested_name: string;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}
export const proposeSchemaMerges = async (sourceId: string): Promise<{ proposals: MergeProposal[]; llm_error?: string }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/views:propose-merges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`propose-merges failed: HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  const body = await res.json() as { proposals: MergeProposal[]; llm_error?: string };
  return { proposals: body.proposals ?? [], llm_error: body.llm_error };
};

export const listSourceErrors = (sourceId: string, limit = 50, includeArchived = false) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (includeArchived) params.set('include_archived', '1');
  return get<TriggerRun[]>(`/api/v1/sources/${sourceId}/runs:errors?${params.toString()}`);
};

/** Archive a single failed run — operator's "I fixed this" action. The
 *  run record is preserved (audit + replay possible); listSourceErrors
 *  filters it out by default. Pair with `unarchiveTriggerRun` for undo. */
export const archiveTriggerRun = (sourceId: string, runId: string) =>
  post<TriggerRun>(`/api/v1/sources/${sourceId}/runs/${runId}/archive`, {});

export const unarchiveTriggerRun = (sourceId: string, runId: string) =>
  post<TriggerRun>(`/api/v1/sources/${sourceId}/runs/${runId}/unarchive`, {});

// ── FilterTree views (V2 recipe-builder views) ───────────────────────


// Views

export const listViews = (sourceId: string) =>
  get<View[]>(`/api/v1/sources/${sourceId}/views`);

export interface ViewHealth {
  view_id: string;
  name: string;
  sampled: number;
  matched_count: number;
  status: 'ok' | 'no_matches' | 'no_events_yet';
}
export interface ViewsHealthResponse {
  source_id: string;
  sampled_window_hours: number;
  sampled: number;
  views: ViewHealth[];
}
export const listViewsHealth = (sourceId: string) =>
  get<ViewsHealthResponse>(`/api/v1/sources/${sourceId}/views:health`);

export const createView = (sourceId: string, body: { name?: string; color?: string; filter: FilterTree; columns?: ColumnSpec[] }) =>
  post<View>(`/api/v1/sources/${sourceId}/views`, body);

export const archiveView = async (sourceId: string, viewId: string): Promise<void> => {
  await fetch(`/api/v1/sources/${sourceId}/views/${viewId}`, { method: 'DELETE' });
};

export const patchView = async (sourceId: string, viewId: string, patch: { name?: string; color?: string; filter?: FilterTree; columns?: ColumnSpec[]; order?: number }): Promise<View> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/views/${viewId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<View>;
};

// ── Triggers + replays + reprocess + lineage ─────────────────────────


// Triggers

export const listTriggers = (sourceId: string) =>
  get<Trigger[]>(`/api/v1/sources/${sourceId}/triggers`);

export const createTrigger = (sourceId: string, body: { name?: string; action: TriggerAction; view_id?: string }) =>
  post<Trigger>(`/api/v1/sources/${sourceId}/triggers`, body);

export const archiveTrigger = async (sourceId: string, triggerId: string): Promise<void> => {
  await fetch(`/api/v1/sources/${sourceId}/triggers/${triggerId}`, { method: 'DELETE' });
};

export const pauseAllTriggers = async (sourceId: string): Promise<{ paused: number }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/triggers:pause-all`, { method: 'POST' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ paused: number }>;
};

export const getTriggerTableCount = (sourceId: string, triggerId: string) =>
  get<{ trigger_id: string; table_name: string; row_count: number; dataset?: string; fully_qualified?: string }>(`/api/v1/sources/${sourceId}/triggers/${triggerId}/table-count`);

export const dryRunTrigger = async (sourceId: string, body: { action: TriggerAction; sample_payload?: Record<string, unknown>; sample_event_id?: string }): Promise<Record<string, unknown>> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/triggers:dry-run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(err.error?.detail ?? err.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Record<string, unknown>>;
};

export const patchTrigger = async (sourceId: string, triggerId: string, patch: { name?: string; status?: 'active' | 'paused' }): Promise<Trigger> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/triggers/${triggerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<Trigger>;
};

export const listTriggerRuns = (sourceId: string, triggerId: string, limit = 20) =>
  get<TriggerRun[]>(`/api/v1/sources/${sourceId}/triggers/${triggerId}/runs?limit=${limit}`);

// Replays

export const listReplays = (sourceId: string) =>
  get<Replay[]>(`/api/v1/sources/${sourceId}/replays`);

export interface SourceStorageStats {
  source_id: string;
  total_event_blobs: number;
  total_batch_blobs: number;
  approximate_event_count: number;
  oldest_date?: string;
  newest_date?: string;
  by_date: Array<{ date: string; per_event_blobs: number; batch_blobs: number; approx_events: number }>;
  // Which tier served the response (BACKLOG.md storage uplift P2). 'object_events'
  // means the GCS Insights BQ table answered (cheap); 'bucket_walk' means we
  // fell back to the legacy bucket walk. UI surfaces this so operators know.
  source_tier?: 'object_events' | 'bucket_walk';
}
export const getSourceStorageStats = (sourceId: string) =>
  get<SourceStorageStats>(`/api/v1/sources/${sourceId}/storage-stats`);

// Object Context replay filter — mirrors ReplayMetadataFilter in @datahub/shared.
// Optional on every replay; when present the worker takes the T1 metadata-LIST
// path so depth/origin/trigger/tag predicates prune the scan up front.
export interface ReplayMetadataFilterClient {
  derivation_depth_max?: number;
  origin_kinds?: Array<'input' | 'trigger'>;
  trigger_ids?: string[];
  trigger_versions?: number[];
  parent_event_ids?: string[];
  tags?: Record<string, string[]>;
}

// Object Context T1 :count — live count + distribution preview without
// downloading any payload. Used by Replay dialog and Dataset Builder.
export interface DatasetCountResult {
  count_approximate: number;
  count_capped: boolean;
  distribution_by_source: Array<{ source_id: string; count: number }>;
  distribution_by_date: Array<{ date: string; count: number }>;
  // Deterministic identity for "this filter applied to this state".
  // ds_<24 hex chars>. Same filter + same bucket state ⇒ same hash.
  dataset_hash: string;
  tier_used: 't1';
}
export const datasetsCount = async (filter: {
  source_ids?: string[];
  received_from?: string;
  received_to?: string;
  derivation_depth_max?: number;
  origin_kinds?: Array<'input' | 'trigger'>;
  trigger_ids?: string[];
  trigger_versions?: number[];
  parent_event_ids?: string[];
  tags?: Record<string, string[]>;
}): Promise<DatasetCountResult> => {
  const r = await fetch('/api/v1/datasets:count', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filter),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
  }
  return r.json() as Promise<DatasetCountResult>;
};

export const createReplayAll = async (sourceId: string, dryRun = false, viewId?: string, metadataFilter?: ReplayMetadataFilterClient): Promise<{ replay_id: string; from: string; to: string }> => {
  const res = await fetch(`/api/v1/sources/${sourceId}/replays`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ replay_all: true, dry_run: dryRun, view_id: viewId, metadata_filter: metadataFilter }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<{ replay_id: string; from: string; to: string }>;
};

export const createReplay = (sourceId: string, body: { from: string; to: string; dry_run: boolean; view_id?: string; metadata_filter?: ReplayMetadataFilterClient }) =>
  post<Replay>(`/api/v1/sources/${sourceId}/replays`, body);

export const getReplay = (sourceId: string, replayId: string) =>
  get<Replay>(`/api/v1/sources/${sourceId}/replays/${replayId}`);

// Partner Portal — public, looked up by portal_token

export interface PortalSourceView {
  source_id: string;
  name: string;
  created_at: string;
  input_count: number;
  has_email: boolean;
  webhook_url: string;
  hooks?: Array<{
    input_id: string;
    name?: string;
    url: string;
    tags?: Record<string, string>;
  }>;
}
export interface PortalIntegration {
  webhook_url: string;
  method: string;
  content_type: string;
  hmac: { header: string; algorithm: string; hint: string };
}

export const portalSource = (token: string) => get<PortalSourceView>(`/portal-api/sources/${token}`);
export const portalEvents = (token: string, limit = 50) =>
  get<DataHubEvent[]>(`/portal-api/sources/${token}/events?limit=${limit}`);
export const portalIntegration = (token: string) =>
  get<PortalIntegration>(`/portal-api/sources/${token}/integration`);

// ── Auto-connect-bq + notifications (Minimum-action onboarding) ──────────────

export interface AutoConnectBqView {
  event_type: string;
  count: number;
  dataset_hash: string;
  bq_dataset: string;
  external_table: string;
  view: string;
  fully_qualified_view: string;
  sample_sql: string;
  links: { looker: string | null; sheets: string | null; bq: string | null };
  object_count: number;
  created: 'created' | 'updated' | 'unchanged';
  cost_estimate?: {
    total_bytes_approx: number;
    avg_bytes_per_event: number;
    t2_query_cost_usd_per_full_scan: number;
    t3_storage_cost_usd_per_month_if_promoted: number;
    t3_promote_breakeven_queries_per_month: number | null;
    hint: string;
  };
}
export interface AutoConnectBqResult {
  source_id: string;
  bq_dataset?: string;
  views: AutoConnectBqView[];
  skipped: Array<{ event_type: string; reason: string }>;
  total_events_sampled: number;
  distinct_event_types: number;
  threshold: number;
}

export const autoConnectBq = async (
  sourceId: string,
  body: { event_types?: string[]; min_events_threshold?: number; bq_dataset?: string; include_untyped?: boolean; dry_run?: boolean } = {},
): Promise<AutoConnectBqResult> => {
  const r = await fetch(`/api/v1/sources/${sourceId}/auto-connect-bq`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = await r.json();
  if (!r.ok) {
    const e = j as { error?: { code?: string; detail?: string } };
    throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
  }
  return j as AutoConnectBqResult;
};

export interface SchemaNotification {
  notification_id: string;
  source_id: string;
  kind: 'schema_ready';
  event_type: string;
  count: number;
  threshold: number;
  created_at: string;
  acknowledged_at?: string;
}

export const listNotifications = async (sourceId: string, includeAcknowledged = false): Promise<SchemaNotification[]> => {
  const url = `/api/v1/sources/${sourceId}/notifications${includeAcknowledged ? '?include_acknowledged=true' : ''}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json() as { notifications: SchemaNotification[] };
  return j.notifications;
};

export const acknowledgeNotificationApi = async (sourceId: string, notificationId: string): Promise<SchemaNotification> => {
  const r = await fetch(`/api/v1/sources/${sourceId}/notifications/${notificationId}:ack`, { method: 'POST' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<SchemaNotification>;
};
